import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getDeletePhysicalSocketDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/delete-physical-socket.form-default-values';
import { DeletePhysicalSocketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/delete-physical-socket.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoDeletePhysicalSocketMutation } from '@service/cpms-bo-charge-point.service';

import {
  DeleteChargePointChannelPhysicalSocketContextProvider,
  useChargePointChannelContext,
  useChargePointChannelPhysicalSocketContext,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointChannelPhysicalSocketDeleteFormController } from './charge-point-channel-physical-socket-delete-form/charge-point-channel-physical-socket-delete-form.controller';

interface ChargePointChannelPhysicalSocketDeleteControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointChannelPhysicalSocketDeleteController = ({
  onCancel,
  onSuccess,
}: ChargePointChannelPhysicalSocketDeleteControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [deletePhysicalSocketModel] = useCpmsBoDeletePhysicalSocketMutation();
  const chargePoint = useChargePointContext();
  const channel = useChargePointChannelContext();
  const physicalSocket = useChargePointChannelPhysicalSocketContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<
      DeletePhysicalSocketModel,
      'chargePointId' | 'channelId' | 'physicalSocketId'
    >
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-channel-physical-socket-delete.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<
      DeletePhysicalSocketModel,
      'chargePointId' | 'channelId' | 'physicalSocketId'
    >
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    deletePhysicalSocketModel({
      chargePointId: fieldValues.chargePointId,
      channelId: fieldValues.channelId,
      physicalSocketId: fieldValues.physicalSocketId,
    })
      .unwrap()
      .then(() => {
        toast.success(
          t('$.charge-point-channel-physical-socket-delete.success'),
        );
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.charge-point-channel-physical-socket-delete.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getDeletePhysicalSocketDefaultValues(
    chargePoint.id,
    channel.id,
    physicalSocket.id,
  );

  return (
    <DeleteChargePointChannelPhysicalSocketContextProvider data={data}>
      <ChargePointChannelPhysicalSocketDeleteFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </DeleteChargePointChannelPhysicalSocketContextProvider>
  );
};
